import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { usePadPreparationStore } from 'src/pages/well-pad-preparation/use-pad-preparation-store';
import Collapse, { Panel } from 'src/shared/components/collapse/collapse';
import { hasValue } from 'src/shared/utils/common';
import type { PadPreparation } from 'src/store/well-pad-preparation/entities';

import { PadDetails } from '../pad-details';

import { PadCollapseHeader } from './pad-collapse-header';
import styles from './pad-collapse.module.scss';

type Props = {
  isExpanded: boolean;
  pad: PadPreparation;
  onExpandedChange(isExpanded: boolean): void;
};

export const PadCollapse = observer<Props>(function PBKCollapse({
  pad,
  isExpanded: _isExpanded,
  onExpandedChange: _onExpandedChange,
}) {
  const [isExpanded, setIsExpanded] = useState(_isExpanded);
  const { isEditModeAvailable, editingPadId, setEditingPadId, applyPadChanges } = usePadPreparationStore();

  const isPadInEditMode = hasValue(editingPadId) && pad.id === editingPadId;
  const activeKey = (() => {
    if (isPadInEditMode) {
      return pad.id;
    }

    return isExpanded ? pad.id : undefined;
  })();

  const onExpandedChange = () => {
    _onExpandedChange(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  return (
    <Collapse activeKey={activeKey} onChange={onExpandedChange}>
      <Panel
        key={pad.id}
        header={
          <PadCollapseHeader
            pad={pad}
            isEditingBlocked={hasValue(editingPadId) && pad.id !== editingPadId}
            isEditing={isPadInEditMode}
            isEditModeAvailable={isEditModeAvailable}
            onEdit={setEditingPadId}
            onCancel={() => {
              pad.restoreInitialValues();
              setEditingPadId(null);
            }}
            onApply={() => {
              applyPadChanges(pad);
            }}
          />
        }
        className={styles.panel}
      >
        <PadDetails isEditing={isPadInEditMode} pad={pad} />
      </Panel>
    </Collapse>
  );
});
